// third-party
import { createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { filterQueryString } from 'helpers/objectFunctions';

// project imports
import { ARYABHATTA_API_DOMAIN_V1 } from 'config';
import { devConsolelog } from 'helpers/utilityFunctions';
import axios from 'utils/axios';
import { DEFAULT_ERROR_MESSAGE } from '../constant';
import { dispatch } from '../index';
import { openSnackbar } from './snackbar';

// ----------------------------------------------------------------------

const defaultStartDate = new Date(Date.now());
const defaultEndDate = new Date(Date.now());

const NO_ORGANIZATION_DETAILS_MSG = 'No Organization detail exist';

const initialState = {
    error: null,
    data: {
        rechargeHistory: {},
        orgDetails: {},
        bookingTarget: {}
    },
    filters: {
        created_at: {
            start_date: format(new Date(defaultStartDate.setDate(defaultStartDate.getDate() - 29)), 'yyyy-MM-dd'),
            end_date: format(defaultEndDate, 'yyyy-MM-dd'),
            isDateFilter: true
        }
    },
    utilities: {},
    autosuggestCityData: {},
    isPostCallForOrganization: false
};

const slice = createSlice({
    name: 'memberDetails',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        setIsLoading(state, action) {
            const { loadingField = '', isLoading = false } = action.payload || {};
            state[loadingField] = isLoading;
        },

        // setData
        setData(state, action) {
            devConsolelog('data in state mem', action.payload);
            const { member_details: memberDetails = {}, bookings = {}, requirements = {}, searches = {} } = action.payload;
            state.data.memberDetails = memberDetails;
            state.data.bookings = bookings;
            state.data.requirements = requirements;
            state.data.searches = searches;
        },

        setRecentActivityData(state, action) {
            devConsolelog('recent activities', action.payload);
            const { latest_five_activities: latestFiveActivities = {} } = action.payload;
            state.data.latestFiveActivities = latestFiveActivities;
        },

        setMemberRechargeData(state, action) {
            const { count = 0, next = null, previous = null, results = [], page = 0 } = action.payload;

            state.data.rechargeHistory.count = count;
            state.data.rechargeHistory.next = next;
            state.data.rechargeHistory.previous = previous;
            state.data.rechargeHistory.rechargeHistoryData = results;
            state.data.rechargeHistory.currentPage = page;
        },

        setOrgDetails(state, action) {
            const { destinations = [] } = action.payload || {};

            const internationalData = destinations.filter((destinations) => {
                const { type = '' } = destinations;
                return type === 'international';
            });

            const domesticData = destinations.filter((destinations) => {
                const { type = '' } = destinations;
                return type === 'india';
            });

            state.data.orgDetails = action.payload;
            state.data.orgDetails.internationalDestination = internationalData || [];
            state.data.orgDetails.domesticDestination = domesticData || [];
        },

        clearOrgDetails(state) {
            state.data.orgDetails = {};
        },

        setAutosuggestCityData(state, action) {
            const { results = [] } = action.payload;

            const autosuggestDropdownData = results.map((data) => {
                const { name = '' } = data || {};

                return { ...data, label: name };
            });

            state.autosuggestCityData = autosuggestDropdownData;
        },

        setProductSoldData(state, action) {
            state.utilities.productSoldList = action.payload;
        },

        setAcquisitionChannels(state, action) {
            state.utilities.acquisitionChannelList = action.payload;
        },

        setIsPostCallForOrganization(state, action) {
            state.isPostCallForOrganization = action.payload;
        },

        setBookingTarget(state, action) {
            state.data.bookingTarget = action.payload;
        },

        setDestinationData(state, action) {
            const destinations = action.payload;

            const internationalData = Object.values(destinations).filter((destinations) => {
                const { type = '' } = destinations;
                return type === 'international';
            });

            const domesticData = Object.values(destinations).filter((destinations) => {
                const { type = '' } = destinations;
                return type === 'india';
            });

            state.utilities.internationalDestinationList = internationalData;
            state.utilities.domesticDestinationList = domesticData;
        },

        setFilters(state, action) {
            devConsolelog('data in state filters', action.payload, state.filters);
            state.filters = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getMemberDetails(id, filterType = '') {
    return async () => {
        devConsolelog('id from params', id);
        let url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${id}?`;
        if (filterType) {
            const finalFilterQueryString = filterQueryString(filterType);
            devConsolelog('filter url', finalFilterQueryString);
            url = `${url}&${finalFilterQueryString}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getMemberDetailsData', response.data);
            dispatch(slice.actions.setData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getMemberRecentActivityDetails(id) {
    return async () => {
        devConsolelog('member code from params', id);
        const url = `${ARYABHATTA_API_DOMAIN_V1}acm/members/${id}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getMemberDetailsData', response.data);
            dispatch(slice.actions.setRecentActivityData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getOrganizationDetails(orgCode) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isOrgDetailsLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}organization/${orgCode}/organization-details/`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setOrgDetails({ ...response.data }));
            dispatch(slice.actions.setIsPostCallForOrganization(false));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isOrgDetailsLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            if (error?.message === NO_ORGANIZATION_DETAILS_MSG) {
                dispatch(slice.actions.setIsPostCallForOrganization(true));
            }
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isOrgDetailsLoading', isLoading: false }));
        }
    };
}

export function getCityAutosuggestData(str = '') {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}travsys/cities?name=${str}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            dispatch(slice.actions.setAutosuggestCityData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getProductSoldData() {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}travsys/categories`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getProductSoldData', response.data);
            dispatch(slice.actions.setProductSoldData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getAcquisitionChannel() {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}travsys/acquisition-channels`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getAcquisitionChannels', response.data);
            dispatch(slice.actions.setAcquisitionChannels({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getDestinationData() {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}travsys/destinations?is_popular=1`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getDestinationData', response.data);
            dispatch(slice.actions.setDestinationData({ ...response.data }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function patchOrganizationData(orgCode, updatedOrgData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}organization/${orgCode}/organization-details/`;
        try {
            await axios.patch(url, updatedOrgData, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Organization Details Updated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.hasError(''));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function postOrganizationData(orgCode, updatedOrgData) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}organization/${orgCode}/organization-details/`;
        try {
            await axios.post(url, updatedOrgData, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Organization Details Updated`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.hasError(''));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export function getMemberRechargeHistory(pageChangeUrl = '', orgCode = '', newPage = 0) {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isRechargeHistoryLoading', isLoading: true }));
    return async () => {
        let url = `${ARYABHATTA_API_DOMAIN_V1}wallet/transaction-history?lms_organisation_code=${orgCode}`;
        if (pageChangeUrl) {
            url = pageChangeUrl;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getMemberRechargeHistoryData', response.data);
            dispatch(slice.actions.setMemberRechargeData({ ...response.data, page: newPage || 0 }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRechargeHistoryLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isRechargeHistoryLoading', isLoading: false }));
        }
    };
}

export function getBookingTarget(memberCode = '') {
    dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingTargetLoading', isLoading: true }));
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}members/${memberCode}/booking-targets`;
        try {
            const response = await axios.get(url, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            devConsolelog('response getBookingTarget', response.data);
            dispatch(slice.actions.setBookingTarget({ ...response.data }));
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingTargetLoading', isLoading: false }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {}, detail = '' } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || detail || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.setIsLoading({ loadingField: 'isBookingTargetLoading', isLoading: false }));
        }
    };
}

export function postBookingTarget(memberCode, bookingTarget) {
    return async () => {
        const url = `${ARYABHATTA_API_DOMAIN_V1}members/${memberCode}/booking-targets`;
        try {
            await axios.post(url, bookingTarget, {
                headers: {
                    // Authorization: authHardcodedToken,
                    source: 'mweb'
                }
            });
            await dispatch(
                openSnackbar({
                    open: true,
                    message: `Booking Target Added`,
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    },
                    close: false
                })
            );
            dispatch(slice.actions.hasError(''));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            const { error: errorObj = {} } = error || {};
            const { errorMessage = '' } = errorObj || {};
            dispatch(
                openSnackbar({
                    open: true,
                    message: errorMessage || DEFAULT_ERROR_MESSAGE,
                    variant: 'alert',
                    alert: {
                        color: 'error'
                    },
                    close: false
                })
            );
        }
    };
}

export const filtersData =
    (filter = '') =>
    async () => {
        devConsolelog('in filters123', filter);
        dispatch(slice.actions.setFilters(filter));
    };

export const clearOrgData = () => () => {
    dispatch(slice.actions.clearOrgDetails());
};
